import {
  type Dispatch,
  type SetStateAction,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { SelectInstance } from "react-select";
import Icon from "../Icon/Icon";
import { IconColorE } from "../../models/enums";
import useQueryParams from "../../utils/hooks/useQueryParams";
import { useNavigate } from "react-router";
import AutocompleteInput from "./components/AutocompleteInput/AutocompleteInput";
import CategorySelector from "./components/CategorySelector/CategorySelector";
import setLastValue from "../Search/helpers/setLastValue";
import { useTranslate } from "../../utils/hooks/useTranslate";

import type { SelectI, SelectOptionT } from "../../models/models";

import styles from "./styles.module.scss";

const FILTER_LABEL_QUERY_PARAM_NAME = "filterLabel";
const FILTER_ID_QUERY_PARAM_NAME = "filterId";
interface AutocompleteSearchI {
  isForCampaignsPage?: boolean;
  formID: string;
  refetch?: () => void;
  setShowSearchBar?: Dispatch<SetStateAction<boolean>>;
  withoutRedirect?: boolean;
}

const AutocompleteSearch = memo(
  (props: AutocompleteSearchI) => {
    const { isForCampaignsPage, withoutRedirect, refetch, setShowSearchBar } =
      props;
    const { searchParams, addQueryParam } = useQueryParams();
    const navigate = useNavigate();
    const { t } = useTranslate();
    const autocompleteRef = useRef<SelectInstance<SelectI>>(null);
    const categorySelectorRef = useRef<SelectInstance<SelectI>>(null);
    const [category, setCategory] = useState<SelectOptionT>(null);
    // Flipped by the first real focus or click anywhere in the search area (capture phase, so it
    // catches the category select and the autocomplete input alike). Until then the autocomplete does
    // not download its lookup lists — the category alone is not a reliable signal of intent because
    // useCategory restores one on mount from the URL or localStorage. Spec 34 D1.
    const [isActive, setIsActive] = useState(false);
    const activate = useCallback(() => setIsActive(true), []);

    // search on enter press
    const handleEnterPress = useCallback((e: KeyboardEvent) => {
      if (autocompleteRef.current.focus && e.key === "Enter") {
        handleSearch();
      }
    }, []);

    useEffect(() => {
      // handle "Enter" key press
      document.addEventListener("keydown", handleEnterPress);
      return () => document.removeEventListener("keydown", handleEnterPress);
    }, []);

    const handleSearch = () => {
      // send selected item ID in case if selected option
      // and usual string from input in case  of absent autocomplete list or autocompleted value

      const prevFilterLabel = searchParams.get(FILTER_LABEL_QUERY_PARAM_NAME);
      const prevFilterId = searchParams.get(FILTER_ID_QUERY_PARAM_NAME);

      const selectedOption = (
        autocompleteRef.current.getValue() as SelectOptionT[]
      )?.[0];
      const selectedCategory = (
        categorySelectorRef.current.getValue() as SelectOptionT[]
      )?.[0]?.value;
      const selectedCategoryLabel = (
        categorySelectorRef.current.getValue() as SelectOptionT[]
      )?.[0]?.label;
      const inputValue = autocompleteRef.current?.inputRef?.value;

      const filterLabel = `${t(selectedCategoryLabel)}: ${
        selectedOption?.label || inputValue
      }`;
      const filterId =
        (filterLabel === prevFilterLabel
          ? +prevFilterId
          : +selectedOption?.value) || null;
      const filter =
        selectedCategory === "productionlocation"
          ? selectedOption?.label === "Local"
            ? "Israel"
            : "Abroad"
          : filterId
          ? ""
          : +selectedOption?.label || inputValue;
      const filterType = selectedCategory;
      const sorting = searchParams.get("sorting");

      // update local storage in order to remember selected filter category
      setLastValue(filterType, isForCampaignsPage);

      if (filterLabel) {
        if (withoutRedirect) {
          addQueryParam({
            filterType,
            filter,
            filterId,
            filterLabel,
          });
        } else {
          const url = `/searchresults?sorting=${sorting}&filterType=${filterType}&filter=${filter}&filterId=${filterId}&filterLabel=${filterLabel}`;
          navigate(url);
        }
        refetch?.();
        setShowSearchBar?.(false);
      }
    };

    return (
      <div
        className={styles.wrapper}
        onFocusCapture={activate}
        onPointerDownCapture={activate}
      >
        <CategorySelector
          ref={categorySelectorRef}
          isForCampaignsPage={isForCampaignsPage}
          setCategory={setCategory}
        />
        <AutocompleteInput
          ref={autocompleteRef}
          isForCampaignsPage={isForCampaignsPage}
          autocompleteCategory={category}
          setShowSearchBar={setShowSearchBar}
          isActive={isActive}
        />
        <div className={styles.searchIconWrapper} onClick={handleSearch}>
          <Icon name="search" title="search" color={IconColorE.WHITE} />
        </div>
      </div>
    );
  },
  (prevProps, nextProps) =>
    prevProps.formID === nextProps.formID &&
    prevProps.isForCampaignsPage === nextProps.isForCampaignsPage &&
    prevProps.withoutRedirect === nextProps.withoutRedirect
);

export default AutocompleteSearch;
